import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Truck, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    const storedOrder = sessionStorage.getItem('lastOrder');
    if (storedOrder) {
      setOrderDetails(JSON.parse(storedOrder));
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (!orderDetails) {
    return null;
  }

  const { orderNumber, items, subtotal, shipping, total, customerInfo, orderDate } = orderDetails;
  
  // Calculate estimated delivery (5-7 business days)
  const deliveryDate = new Date(orderDate);
  deliveryDate.setDate(deliveryDate.getDate() + 7);
  const formattedDeliveryDate = deliveryDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-[#FFF9F5]" data-testid="order-confirmation-page">
      <div className="container-main section-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto"
        >
          {/* Success Header */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-20 h-20 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Order Placed Successfully!
            </h1>
            <p className="text-gray-600 text-lg">
              Thank you for your purchase. We've received your order and will process it shortly.
            </p>
          </div>

          {/* Order Info */}
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-6" data-testid="order-info">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6 pb-6 border-b">
              <div>
                <p className="text-gray-500 text-sm">Order Number</p>
                <p className="font-heading font-bold text-xl text-brand-orange">{orderNumber}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500 text-sm">Order Date</p>
                <p className="font-medium">{new Date(orderDate).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}</p>
              </div>
            </div>

            {/* Order Items */}
            <div className="space-y-4 mb-6">
              <h3 className="font-heading font-bold text-lg">Order Items</h3>
              {items.map(item => (
                <div key={item.id} className="flex gap-4 py-3 border-b">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'text-brand-green' : ''}>
                  {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between font-heading font-bold text-lg pt-2 border-t">
                <span>Total</span>
                <span className="text-brand-orange">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-brand-orange-light rounded-xl p-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Payment Method:</span> Cash on Delivery (COD)
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Please have ${total.toFixed(2)} ready when your order arrives.
              </p>
            </div>
          </div>

          {/* Shipping & Delivery Info */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Shipping Address */}
            <div className="bg-white rounded-3xl p-6 shadow-sm" data-testid="shipping-info">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-brand-orange" />
                <h3 className="font-heading font-bold">Shipping Address</h3>
              </div>
              <p className="text-gray-700">
                {customerInfo.firstName} {customerInfo.lastName}<br />
                {customerInfo.address}<br />
                {customerInfo.city}, {customerInfo.state} {customerInfo.zipCode}<br />
                United States
              </p>
            </div>

            {/* Estimated Delivery */}
            <div className="bg-white rounded-3xl p-6 shadow-sm" data-testid="delivery-info">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-brand-orange" />
                <h3 className="font-heading font-bold">Estimated Delivery</h3>
              </div>
              <p className="text-gray-700 mb-2">
                {formattedDeliveryDate}
              </p>
              <p className="text-sm text-gray-500">
                You'll receive tracking information via email once your order ships.
              </p>
            </div>
          </div>

          {/* Customer Support */}
          <div className="bg-white rounded-3xl p-6 shadow-sm mb-8" data-testid="support-info">
            <h3 className="font-heading font-bold text-lg mb-4">Need Help?</h3>
            <p className="text-gray-600 mb-4">
              If you have any questions about your order, our customer support team is here to help.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-brand-orange" />
                <a href="mailto:support@nexafusion.store" className="text-brand-orange hover:underline">
                  support@nexafusion.store
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-brand-orange" />
                <a href="tel:+18887281766" className="text-brand-orange hover:underline">
                  +1-888-728-1766
                </a>
              </div>
            </div>
          </div>

          {/* Continue Shopping */}
          <div className="text-center">
            <Button asChild className="btn-primary" data-testid="continue-shopping-btn">
              <Link to="/">Continue Shopping</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
